// =============================================================================
// DesignPartsSection — per-side print files (apparel) / per-sheet (gangsheet)
// =============================================================================
// Renders the composite/product mockup gallery plus one row per print side
// with a preview thumbnail and a 300 DPI PNG download. The API only includes
// `designParts` for audiences allowed to see them (STOREFRONT custom products
// for the merchant; everything for admin/provider), so callers simply pass the
// `designParts` array through and this block stays hidden otherwise.
//
// This is a read-only presentational component: it has no mutating actions,
// only preview/download links. Shared by the portal order summary, the admin
// provider fulfillment card, and the admin self-fulfillment card so all
// audiences see identical print-file rows for any content-addressed design.

import { clsx } from 'clsx';
import { Badge } from './Badge';

/**
 * A single content-addressed design part as returned by the API.
 * Canonical shape — apps must import this type instead of redefining it.
 */
export interface DesignPartInfo {
  partKey: string;
  kind: 'SHEET' | 'SIDE' | 'COMPOSITE' | (string & {});
  widthIn: number;
  heightIn: number;
  /**
   * Measured (post-trim) physical size of the FINAL export file in inches —
   * the real print size, as opposed to the print-area canvas above. Null /
   * undefined until the export renders (or for rows measured before the
   * column existed).
   */
  exportWidthIn?: number | null;
  exportHeightIn?: number | null;
  previewStatus: string;
  exportStatus: string;
  /** Garment-mockup render status (SIDE parts only; PENDING = n/a). */
  mockupStatus?: string | null;
  previewUrl: string | null;
  exportUrl: string | null;
  /** Per-side garment-mockup JPEG (design composed on the blank photo). */
  mockupUrl?: string | null;
  /** Order-scoped download URL with the production filename (preferred). */
  downloadUrl?: string | null;
}

export interface DesignPartsSectionProps {
  parts?: DesignPartInfo[];
  /** Extra classes for the root container (e.g. spacing tweaks per surface). */
  className?: string;
  /**
   * Pre-rendered preview image to show as the "Product Mockup" when the parts
   * carry no per-side mockup AND no COMPOSITE preview. Used for PRODUCT-context
   * (published POD product) lines, whose designs have only canonical per-side
   * print rows — the single customer-facing image is the ordered variant's hero
   * composite (`ProductVariant.imageUrl`). Ignored when side mockups / a
   * composite preview already exist (apparel).
   */
  previewImageUrl?: string | null;
}

/** Title-case a print-area slug ("front" → "Front", "left_sleeve" → "Left Sleeve"). */
function formatPartLabel(partKey: string): string {
  return partKey
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "12.3" instead of "12.30"/"12" instead of "12.00" — sizes read cleanly. */
function formatInches(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * Whether the measured (post-trim) export size meaningfully differs from the
 * print-area canvas. Gangsheet SHEET exports are never trimmed (the sheet
 * layout IS the print), so their measured size equals the canvas — showing a
 * duplicate "actual size" line there is noise.
 */
function hasDistinctMeasuredSize(p: DesignPartInfo): boolean {
  if (p.exportWidthIn == null || p.exportHeightIn == null) return false;
  return (
    Math.abs(p.exportWidthIn - p.widthIn) > 0.05 ||
    Math.abs(p.exportHeightIn - p.heightIn) > 0.05
  );
}

export function DesignPartsSection({
  parts,
  className,
  previewImageUrl,
}: DesignPartsSectionProps) {
  if (!parts || parts.length === 0) return null;

  const composite = parts.find((p) => p.kind === 'COMPOSITE');
  const printParts = parts.filter((p) => p.kind !== 'COMPOSITE');
  if (printParts.length === 0 && !composite) return null;

  // Prominent "Product Mockup" gallery: per-side garment renders when
  // available, otherwise the composite preview (which is itself the mockup
  // grid for apparel designs), otherwise the supplied PRODUCT-context variant
  // preview (published designs have no composite part of their own).
  const sideMockups = printParts.filter((p) => p.kind === 'SIDE' && p.mockupUrl);
  const mockupFallbackUrl =
    sideMockups.length === 0
      ? composite?.previewUrl ?? previewImageUrl ?? null
      : null;

  return (
    <div
      className={clsx(
        'mt-2 rounded-md border border-border bg-surface-secondary/40 p-2',
        className,
      )}
    >
      {(sideMockups.length > 0 || mockupFallbackUrl) && (
        <div className="mb-2">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            Product Mockup
          </span>
          {sideMockups.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {sideMockups.map((p) => (
                <a
                  key={p.partKey}
                  href={p.mockupUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block overflow-hidden rounded-md border border-border bg-surface"
                  title={`${formatPartLabel(p.partKey)} mockup`}
                >
                  <img
                    src={p.mockupUrl!}
                    alt={`${formatPartLabel(p.partKey)} product mockup`}
                    className="h-24 w-24 object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/45 px-1 py-0.5 text-center text-[10px] font-medium text-white">
                    {formatPartLabel(p.partKey)}
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <a
              href={mockupFallbackUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-fit overflow-hidden rounded-md border border-border bg-surface"
            >
              <img
                src={mockupFallbackUrl!}
                alt="Product mockup"
                className="h-24 w-auto object-contain"
                loading="lazy"
              />
            </a>
          )}
        </div>
      )}
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
          Print files {printParts.length > 0 ? `(${printParts.length})` : ''}
        </span>
      </div>
      <ul className="space-y-1">
        {printParts.map((p) => (
          <li
            key={p.partKey}
            className="flex items-center justify-between gap-2 rounded border border-border bg-surface px-2 py-1"
          >
            <div className="flex min-w-0 items-center gap-2">
              {(() => {
                // Prefer the FINAL (trimmed) print file as the thumbnail once
                // the export is ready — it is what actually ships to print.
                // Fall back to the untrimmed canvas preview while rendering.
                const finalUrl =
                  p.exportStatus === 'READY' && p.exportUrl ? p.exportUrl : null;
                const thumbUrl = finalUrl ?? p.previewUrl;
                if (!thumbUrl) {
                  return (
                    <div className="h-8 w-8 shrink-0 rounded border border-border bg-surface-secondary" />
                  );
                }
                return (
                  <a
                    href={thumbUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0"
                    title={
                      finalUrl
                        ? `Open ${formatPartLabel(p.partKey)} final print file`
                        : `Open ${formatPartLabel(p.partKey)} preview`
                    }
                  >
                    <img
                      src={thumbUrl}
                      alt={
                        finalUrl
                          ? `${formatPartLabel(p.partKey)} final print file`
                          : `${formatPartLabel(p.partKey)} preview`
                      }
                      loading="lazy"
                      className={clsx(
                        'h-8 w-8 rounded border border-border bg-surface-secondary transition-opacity hover:opacity-80',
                        // The trimmed file's aspect ratio IS the artwork —
                        // never crop it; the canvas preview keeps the old crop.
                        finalUrl ? 'object-contain' : 'object-cover',
                      )}
                    />
                  </a>
                );
              })()}
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-text-primary">
                  {formatPartLabel(p.partKey)}
                </p>
                {hasDistinctMeasuredSize(p) ? (
                  <p className="text-[10px] text-text-tertiary">
                    <span className="font-medium text-text-secondary">
                      {formatInches(p.exportWidthIn!)}&times;
                      {formatInches(p.exportHeightIn!)}&quot;
                    </span>{' '}
                    &middot; print area {p.widthIn}&times;{p.heightIn}&quot;
                  </p>
                ) : (
                  <p className="text-[10px] text-text-tertiary">
                    {p.widthIn}&times;{p.heightIn}&quot;
                  </p>
                )}
              </div>
            </div>
            {p.exportStatus === 'READY' && (p.downloadUrl || p.exportUrl) ? (
              <a
                href={p.downloadUrl ?? p.exportUrl ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-text-primary px-2.5 py-1.5 text-[11px] font-medium text-surface transition-opacity hover:opacity-90"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Download Print File (PNG)
              </a>
            ) : p.exportStatus === 'FAILED' ? (
              <Badge variant="danger" size="sm">
                Failed
              </Badge>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-text-secondary">
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                …
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
